import { formatDistanceToNow } from "date-fns";
import { Calendar, Download, FileText, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { useTenant } from "@/contexts/TenantContext";
import { formatDateTime } from "@/lib/utils";

interface AuditLog {
	id: string;
	timestamp: string;
	action: string;
	user: string;
	resource: string;
	resourceType: string;
	ipAddress?: string;
	userAgent?: string;
	details?: Record<string, unknown>;
	severity: "info" | "warning" | "error" | "critical";
}

export function AuditLogsPage() {
	const { tenant } = useTenant();
	const [logs, setLogs] = useState<AuditLog[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [searchQuery, setSearchQuery] = useState("");
	const [selectedSeverity, setSelectedSeverity] = useState<string>("all");
	const [selectedAction, setSelectedAction] = useState<string>("all");
	const [currentPage, setCurrentPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);

	const loadLogs = async () => {
		if (!tenant) return;

		try {
			setIsLoading(true);
			// TODO: Replace with actual API endpoint when available
			// const response = await apiClient.getAuditLogs(tenant.id, {
			//   severity: selectedSeverity === 'all' ? undefined : selectedSeverity,
			//   action: selectedAction === 'all' ? undefined : selectedAction,
			//   search: searchQuery || undefined,
			//   page: currentPage,
			//   limit: 50,
			// })

			// Mock data for now
			const mockLogs: AuditLog[] = [
				{
					id: "1",
					timestamp: new Date(Date.now() - 1000 * 60 * 5).toISOString(),
					action: "package.publish",
					user: "user@example.com",
					resource: "@acme/logger",
					resourceType: "package",
					ipAddress: "192.168.1.1",
					severity: "info",
				},
				{
					id: "2",
					timestamp: new Date(Date.now() - 1000 * 60 * 30).toISOString(),
					action: "token.create",
					user: "admin@example.com",
					resource: "CI/CD Token",
					resourceType: "token",
					ipAddress: "192.168.1.2",
					severity: "warning",
				},
				{
					id: "3",
					timestamp: new Date(Date.now() - 1000 * 60 * 60).toISOString(),
					action: "package.delete",
					user: "user@example.com",
					resource: "@acme/old-package",
					resourceType: "package",
					ipAddress: "192.168.1.1",
					severity: "error",
				},
			];

			setLogs(mockLogs);
			setTotalPages(1);
		} catch (error) {
			console.error("Failed to load audit logs:", error);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		if (tenant) {
			loadLogs();
		}
	}, [tenant, loadLogs]);

	const handleSearch = () => {
		setCurrentPage(1);
		loadLogs();
	};

	const getSeverityColor = (severity: string) => {
		const colors: Record<string, string> = {
			info: "bg-blue-100 text-blue-700",
			warning: "bg-yellow-100 text-yellow-700",
			error: "bg-red-100 text-red-700",
			critical: "bg-red-200 text-red-800",
		};
		return colors[severity] || "bg-gray-100 text-gray-700";
	};

	const filteredLogs = logs.filter((log) => {
		if (searchQuery) {
			const query = searchQuery.toLowerCase();
			return (
				log.action.toLowerCase().includes(query) ||
				log.user.toLowerCase().includes(query) ||
				log.resource.toLowerCase().includes(query)
			);
		}
		return true;
	});

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-2xl font-bold text-gray-900">Audit Logs</h1>
					<p className="text-gray-500 mt-1">
						Track all security-related events and user actions
					</p>
				</div>
				<Button variant="outline">
					<Download className="w-4 h-4 mr-2" />
					Export Logs
				</Button>
			</div>

			{/* Filters */}
			<Card>
				<CardContent className="pt-6">
					<div className="flex flex-col md:flex-row gap-4">
						<div className="flex-1 flex gap-2">
							<div className="relative flex-1">
								<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
								<Input
									placeholder="Search logs..."
									value={searchQuery}
									onChange={(e) => setSearchQuery(e.target.value)}
									onKeyDown={(e) => e.key === "Enter" && handleSearch()}
									className="pl-10"
								/>
							</div>
							<Button onClick={handleSearch}>Search</Button>
						</div>
						<Select
							value={selectedSeverity}
							onValueChange={setSelectedSeverity}
						>
							<SelectTrigger className="w-full md:w-48">
								<SelectValue placeholder="Severity" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">All Severities</SelectItem>
								<SelectItem value="info">Info</SelectItem>
								<SelectItem value="warning">Warning</SelectItem>
								<SelectItem value="error">Error</SelectItem>
								<SelectItem value="critical">Critical</SelectItem>
							</SelectContent>
						</Select>
						<Select value={selectedAction} onValueChange={setSelectedAction}>
							<SelectTrigger className="w-full md:w-48">
								<SelectValue placeholder="Action" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">All Actions</SelectItem>
								<SelectItem value="package.publish">Package Publish</SelectItem>
								<SelectItem value="package.delete">Package Delete</SelectItem>
								<SelectItem value="token.create">Token Create</SelectItem>
								<SelectItem value="token.revoke">Token Revoke</SelectItem>
								<SelectItem value="user.invite">User Invite</SelectItem>
								<SelectItem value="settings.update">Settings Update</SelectItem>
							</SelectContent>
						</Select>
					</div>
				</CardContent>
			</Card>

			{/* Logs table */}
			<Card>
				<CardHeader>
					<CardTitle>Recent Activity</CardTitle>
					<CardDescription>
						Security and administrative events from the last 30 days
					</CardDescription>
				</CardHeader>
				<CardContent>
					{isLoading ? (
						<div className="flex items-center justify-center py-8">
							<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
						</div>
					) : filteredLogs.length === 0 ? (
						<div className="text-center py-12">
							<FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
							<h3 className="text-lg font-medium text-gray-900 mb-2">
								No logs found
							</h3>
							<p className="text-gray-500">
								{searchQuery
									? `No logs match your search criteria`
									: "Audit logging will appear here once enabled"}
							</p>
						</div>
					) : (
						<div className="overflow-x-auto">
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>Timestamp</TableHead>
										<TableHead>Action</TableHead>
										<TableHead>User</TableHead>
										<TableHead>Resource</TableHead>
										<TableHead>Severity</TableHead>
										<TableHead>IP Address</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{filteredLogs.map((log) => (
										<TableRow key={log.id}>
											<TableCell>
												<div className="flex items-center space-x-2">
													<Calendar className="w-4 h-4 text-gray-400" />
													<div>
														<div className="text-sm font-medium">
															{formatDateTime(log.timestamp)}
														</div>
														<div className="text-xs text-gray-500">
															{formatDistanceToNow(new Date(log.timestamp), {
																addSuffix: true,
															})}
														</div>
													</div>
												</div>
											</TableCell>
											<TableCell>
												<code className="text-sm bg-gray-100 px-2 py-1 rounded">
													{log.action}
												</code>
											</TableCell>
											<TableCell>
												<div className="font-medium">{log.user}</div>
											</TableCell>
											<TableCell>
												<div>
													<div className="font-medium">{log.resource}</div>
													<div className="text-xs text-gray-500">
														{log.resourceType}
													</div>
												</div>
											</TableCell>
											<TableCell>
												<Badge className={getSeverityColor(log.severity)}>
													{log.severity}
												</Badge>
											</TableCell>
											<TableCell>
												<code className="text-xs text-gray-500">
													{log.ipAddress || "N/A"}
												</code>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</div>
					)}

					{/* Pagination */}
					{totalPages > 1 && (
						<div className="flex items-center justify-between mt-4 pt-4 border-t">
							<div className="text-sm text-gray-500">
								Page {currentPage} of {totalPages}
							</div>
							<div className="flex gap-2">
								<Button
									variant="outline"
									size="sm"
									disabled={currentPage === 1}
									onClick={() => setCurrentPage(currentPage - 1)}
								>
									Previous
								</Button>
								<Button
									variant="outline"
									size="sm"
									disabled={currentPage === totalPages}
									onClick={() => setCurrentPage(currentPage + 1)}
								>
									Next
								</Button>
							</div>
						</div>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
